// Shared, framework-agnostic formatter for the client-side login-error-page telemetry lines.
//
// The lines follow the same space-delimited `key=value` scheme used by InvitationIssues::Result
// (accept-invitation-api/invitation-log) so the report infrastructure can parse both with the same
// logic. Every line carries `page` (which error page) and `outcome` (what happened — page shown or
// button clicked), plus optional `reason` and `email` for correlation.
//
// This module is pure string building (no React, no logger) so it can be reused anywhere.

// The stable prefix the report filters on.
export const LOGIN_ERROR_LOG_PREFIX = 'LoginErrorIssues::Result';

export type LoginErrorPage =
  | 'NoAccount'
  | 'WorkAccountRequired'
  | 'PersonalAccountRequired'
  | 'GoogleAccountRequired'
  | 'MicrosoftAccountRequired'
  | 'AadEnforcement'
  | 'AadSwitchComplete'
  | 'AadSignInRequired';

export type LoginErrorOutcome =
  | 'PageShown'
  | 'ClickTryDifferentEmail'
  | 'ClickCreateAccount'
  | 'ClickContinue'
  | 'ClickContinueToAdvertising'
  | 'ClickNext'
  | 'ClickSwitchAccount'
  | 'SwitchUrlMissing'
  | 'ClickLearnMore'
  | 'ClickBack'
  | 'ClickGoBack';

export type LoginErrorLogFields = {
  page: LoginErrorPage;
  outcome: LoginErrorOutcome;
  // The raw reason query param from the URL (e.g. 'noAccount', 'aadrequired').
  reason?: string;
  // The user's email address if available.
  email?: string;
  // Optional extra key=value context (e.g. { idP: 'AAD' }). Null/undefined values are skipped.
  extra?: Record<string, string | number | boolean | null | undefined>;
};

// Builds a single telemetry line, e.g.
//   LoginErrorIssues::Result page=NoAccount outcome=PageShown reason=noAccount email=user%40example.com
//
// Dynamic values (reason, email, and every extra value) are URL-encoded before being appended.
// The line is space-delimited `key=value`, and the report parser splits on whitespace; a value
// containing a space would otherwise inject extra tokens and break downstream parsing.
// `page` and `outcome` are internal enums (never dynamic) so they are left as-is.
export const buildLoginErrorLog = ({
  page, outcome, reason, email, extra,
}: LoginErrorLogFields): string => {
  const parts = [
    LOGIN_ERROR_LOG_PREFIX,
    `page=${page}`,
    `outcome=${outcome}`,
  ];

  if (reason) {
    parts.push(`reason=${encodeURIComponent(reason)}`);
  }

  if (email) {
    parts.push(`email=${encodeURIComponent(email)}`);
  }

  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        parts.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    });
  }

  return parts.join(' ');
};
